import { gql } from "@apollo/client";
import useAppContext from "../../../../utilities/hooks/useAppContext";
import Datalist, { DatalistPropsType } from "../../../common/datalist/Datalist";
import {
	BookingEventFilterInput,
	BookingEventSortInput,
	EventDatalistQuery,
	EventDatalistQueryVariables,
	SortEnumType
} from "../../../../types/graphql";
import { graph } from "../../../../config/axios-context";
import { appendFilters } from "../../../../utilities/datalist/datalist-filter-utilities";
import { DateComparisons, DateFormats, DateUtil } from "../../../../utilities/date-utilities";
import { faBed, faCalendar, faHashtag, faPlus, faUmbrellaBeach } from "@fortawesome/free-solid-svg-icons";
import RoomTypeView from "../roomType/RoomTypeView";
import BooleanView from "../../../common/views/BooleanView";
import { Roles } from "utilities/app-utilities";
import ModalPortal from "components/modal/ModalPortal";
import EventForm from "./EventForm";
import { useRef } from "react";
import * as lodash from 'lodash';
import EventView from "./EventView";
import { useSearchParams } from "react-router-dom";
import Translate from "components/common/Translate";
import BookingIdView from "../booking/views/BookingIdView";
import EventDatalistActions from "./partial/EventDatalistActions";
import { changeEventIsDone } from "utilities/entities/accommodation/event-utilities";
import EventNameView from "./partial/EventNameView";
import EventDateView from "./partial/EventDateView";
import { DatalistColumnDefinitions } from "utilities/datalist/datalist-utilities";
import { uuid } from "config/app-constants";
import { DeepPartial } from "@apollo/client/utilities";

const ADDz_MODAL_ID = 'add-event-modal';
const VIEWz_MODAL_ID = 'view-event-modal';

export type AccommodationEventDatalistType = {
	config?: DeepPartial<DatalistPropsType<ListItem, BookingEventFilterInput, BookingEventSortInput>>
	fixedFilters?: BookingEventFilterInput,
	addNew?: boolean,
	name: string
	hideActions?: boolean,
	bookingId?: number | null
}

type ListItem = NonNullable<EventDatalistQuery['bookingEvents']>['items'][0];

const query = gql`
query EventDatalist($where: BookingEventFilterInput, $order: [BookingEventSortInput!], $skip: Int, $take:Int)
{
  bookingEvents {
    count(where: $where)
    items(where: $where, order: $order, skip: $skip, take: $take) {
      id
      date
      note
      isDone
      insertType
      name,
      group
      order,
	  assigneeId,
	  assignee {
		id
		name
	  },
      booking {
        id
        inDate
        outDate
        inSeasonId
        seasonId
        nights
        guestCount,
        children
        season {
            id
            name
            active
        }
        person {
          id
          name
        }
        details {
          items
          {
            date
            room {
              id
              name,
              roomType {
                color
                name
                id
              }
            }
          }
        }
      }
    }
  }
}
`;

const AccommodationEventDatalist = (props: AccommodationEventDatalistType) => {
	const context = useAppContext();
	const [searchParams] = useSearchParams();
	const dataListRef = useRef<Datalist<ListItem, BookingEventFilterInput, BookingEventSortInput>>(null);
	const addModalId = `${props.name}-${ADDz_MODAL_ID}`;
	const viewModalId = `${props.name}-${VIEWz_MODAL_ID}`;
	const defaultConfig: DatalistPropsType<ListItem | string, BookingEventFilterInput, BookingEventSortInput> = {
		itemId: i => typeof (i) == 'string' ? i : i.id,
		name: 'booking-event-datalist',
		dataSource: {
			request: async params => {
				try {
					let variables: EventDatalistQueryVariables = {
						order: params.sort || [],
						skip: params.skip,
						take: params.take,
						where: params.filters
					}

					if (!Array.isArray(variables.order)) {
						variables.order = [variables.order as any];
					}

					variables.order.unshift({
						date: SortEnumType.Asc
					})


					if (props.fixedFilters) {
						variables.where = appendFilters(variables.where, props.fixedFilters)
					}

					if (params.searchText) {
						const searchTextFilter: BookingEventFilterInput = {
							or: [
								{
									name: {
										contains: params.searchText
									}
								}
							]
						};

						variables.where = appendFilters(variables.where, searchTextFilter)
					}

					const response = await graph<EventDatalistQuery, EventDatalistQueryVariables>({
						query,
						variables
					});

					if (response.errors?.length) {
						throw response.errors;
					}

					const data: Array<ListItem | string> = [];
					let currentDate: Date | undefined = undefined;
					response.data!.bookingEvents!.items.forEach(i => {
						if (!currentDate || !DateUtil.compareOnAsIsoDate(i.date, currentDate)) {
							data.push(i.date);
							currentDate = lodash.clone(i.date);
						}

						data.push(i);
					});

					return {
						count: response!.data!.bookingEvents!.count,
						items: data
					};
				} catch (e: any) {
					context.notify('error', 'error.loading-data', Array.isArray(e) ? e[0].message : e.message);
					throw e;
				}
			},
			sort: [
				{
					id: uuid(),
					name: "isDone",
					direction: SortEnumType.Asc
				},
				{
					id: uuid(),
					name: 'order',
					direction: SortEnumType.Asc
				},
				{
					id: uuid(),
					name: 'name',
					direction: SortEnumType.Asc
				}
			]
		},
		// defaultView: 'card',
		table: {
			render: {
				theme: "none",
				card(item) {
					if (typeof item == 'string') {
						return <div key={item} className="w-full p-1 text-lg font-bold app-label sticky top-0 bg-white dark:bg-black shadow-sm">
							<EventDateView date={item as any} />
						</div>
					}

					let bookingView = <></>;
					if (item.booking) {
						const detail = item.booking.details.items.find(x => DateUtil.compareOnAsIsoDate(item.date, x.date));
						const value = {
							color: detail?.room.roomType.color || item.booking.details.items[item.booking.details.items.length - 1].room.roomType.color,
							name: detail?.room.name || item.booking.details.items[item.booking.details.items.length - 1].room.name
						};
						const translationModel = {
							nights: item.booking.nights,
							guests: item.booking.guestCount,
							children: item.booking.children || '0'
						};

						const translationKey = translationModel.children && translationModel.children !== '0'
							? 'accommodation.event.bookingDataStringChildren' : 'accommodation.event.bookingDataStringNoChildren';
						bookingView = <>
							<div>
								<span>{item.booking.person.name}</span>
							</div>
							<div>
								<span>{DateUtil.format(item.booking.inDate, DateFormats.SHORT)} - {DateUtil.format(item.booking.outDate, DateFormats.SHORT)}</span>
							</div>
							<div className="text-sm">
								<span>#{item.booking.inSeasonId}</span>
								<span className="mx-2">-</span>
								<RoomTypeView {...value} />
								<span className="mx-2">-</span>
								<span>{context.t(translationKey, translationModel)}</span>
							</div>
							{item.assignee && <div>
								<b>-- {item.assignee?.name}</b>
							</div>}
						</>
					}

					let borderColor = getBorderColor(item);

					return (
						<div key={item.id} className={`w-full border px-4 py-2 mb-4 hover:border-sunrise-300 ${borderColor}`}>
							<div className="flex">
								<div className="block pr-4 m-auto">
									<BooleanView square value={item.isDone} />
								</div>
								<div className="w-full">
									<div>
										<span><EventNameView item={item} /></span>
									</div>
									{bookingView}
								</div>
								{!props.hideActions && <div className="text-left flex">
									<EventDatalistActions item={item} viewModalId={viewModalId} onChangeIsDone={onChangeIsDone} />
								</div>}
							</div>
						</div>
					);
				},
				row: {
					className: (item) => {
						if (typeof item == 'string') return '';
					}
				},
			},
			columns: [
				{ // id
					name: 'id',
					filter: {
						type: 'number'
					},
					type: 'number',
					hide: true,
					card: {
						icon: faHashtag,
						group: 'id'
					}
				},
				{ // forcolor
					name: 'forColor',
					title: '',
					resolve: item => {
						if (typeof item == 'string') return '';
						let borderColor = undefined;
						if (item.isDone) {
							borderColor = 'bg-green-500';
						} else if (DateUtil.compareOnAsIsoDate(item.date, new Date(), DateComparisons.LOWER) && !item.isDone) {
							borderColor = 'bg-red-500';
						} else if (DateUtil.compareOnAsIsoDate(item.date, new Date())) {
							borderColor = 'bg-blue-500';
						}

						return <div className={`ml-auto w-1 h-6 ${borderColor}`}></div>
					},
					columnClassName(item, index) {
						if (typeof item == 'string') return '';
						return 'text-right p-0'
					},
				},
				{ // date
					name: 'date',
					filter: {
						type: 'dateOnly',
					},
					hide: true,
					type: 'dateOnly',
					card: {
						icon: faCalendar,
						group: 'id'
					}
				},
				{ // name
					name: 'name',
					type: 'string',
					resolve: i => {
						if (typeof i === 'string') {
							return <EventDateView date={i as any} />
						}

						return <EventNameView item={i} />
					},
					colSpan: i => {
						return typeof i == 'string' ? 6 : undefined
					},
					columnClassName: 'text-left',
					filter: {
						type: 'string'
					},
					sort: true
				},
				DatalistColumnDefinitions.personColumnDefinitionType(context, ["booking", "person", "name"]),
				DatalistColumnDefinitions.personColumnDefinitionType(context, ["assignee", "name"], {
					name: 'assignee',
					title: 'fields.assignee.name',
				}),
				{ // bookingDates
					name: 'bookingDates',
					resolve: i => typeof i === 'string' || !i.booking ? '' : <span>{formatDate(i.booking.inDate)} - {formatDate(i.booking.outDate)} ({i.booking.nights})</span>,
				},
				{ // booking
					name: 'booking',
					resolve: i => typeof i === 'string' || !i.booking ? '' : <BookingIdView inSeasonId={i.booking.inSeasonId} id={i.booking.id} />,
					card: {
						group: 'booking',
						icon: faUmbrellaBeach
					}
				},
				{ // room
					name: 'room',
					resolve: i => {
						if (typeof i === 'string' || !i.booking) return '';
						const detail = i.booking.details.items.find(x => DateUtil.compareOnAsIsoDate(i.date, x.date));
						const value = {
							color: detail?.room.roomType.color || i.booking.details.items[i.booking.details.items.length - 1].room.roomType.color,
							name: detail?.room.name || i.booking.details.items[i.booking.details.items.length - 1].room.name
						};

						return <RoomTypeView {...value} />
					},
					card: {
						group: 'id',
						icon: faBed
					}
				},
				{ // isDone
					name: 'isDone',
					title: 'fields.is-done.name',
					type: 'boolean',
					resolve: i => typeof i === 'string' ? '' : <BooleanView square value={i.isDone} />,
					filter: {
						type: 'boolean'
					},
					sort: true,
					card: {
						group: 'booking',
						resolve: i => {
							if (typeof i === 'string') return '';
							return i.isDone ? <span className="p-1 bg-success text-white"><Translate i18nKey="accommodation.event.done" /> </span> :
								<span className="p-1 bg-danger text-white"><Translate i18nKey="accommodation.event.notDone" /></span>
						}
					}
				},
				{ // season
					...DatalistColumnDefinitions.seasonColumnDefinitionType(context, ["booking", "season"], undefined, true),
					hide: true
				},
				{ // actionsEvent
					name: 'actionsEvent',
					title: '',
					hide: props.hideActions,
					resolve: i => typeof i === 'string' ? '' : <EventDatalistActions item={i} viewModalId={viewModalId} onChangeIsDone={onChangeIsDone} />,
				},
				{ // order
					name: 'order',
					title: '',
					hide: true,
					sort: {
						title: "fields.order.name",
						resolve(direction) {
							return {
								order: direction
							}
						},
					}
				}
			]
		},
		header: {
			rightButtons: [
				{
					icon: faPlus,
					name: 'add',
					title: 'button.add',
					onClick: () => context.modal.open(addModalId),
					hide: !props.addNew || !context.userHasRole(Roles.ACCOMMODATION_BOOKINGS_WRITE)
				}
			]
		}
	}

	const config = lodash.merge(defaultConfig, props.config) as DatalistPropsType<ListItem, BookingEventFilterInput, BookingEventSortInput>;

	const getBorderColor = (item: ListItem) => {
		let borderColor = 'theme-border';
		if (item.isDone) {
			borderColor = 'border-green-500';
		} else if (DateUtil.compareOnAsIsoDate(item.date, new Date(), DateComparisons.LOWER)) {
			borderColor = 'border-red-500';
		} else if (DateUtil.compareOnAsIsoDate(item.date, new Date())) {
			borderColor = 'border-blue-500';
		}
		return borderColor;
	}

	const onUpdate = (modalId: string) => {
		context.modal.close(modalId);
		dataListRef.current?.reload();
	}

	const onChangeIsDone = async (id: number, isDone: boolean) => {
		try {
			const response = await changeEventIsDone({ id, isDone });

			if (response.success) {
				context.notify('success', 'request.save.success');
				dataListRef.current?.reload();
			}

		} catch (error: any) {
			context.notify('error', 'request.load.error', Array.isArray(error) ? error[0].message : error.message);
			throw error;
		}
	};

	const formatDate = (date: Date) => {
		return DateUtil.format(date, DateFormats.SHORT);
	}

	return <>
		<Datalist {...config} ref={dataListRef} />
		<ModalPortal name={addModalId} title="pages.accommodation.event.form">
			{searchParams.has(addModalId) ? (
				<EventForm
					name="accommodationEventDatalist"
					bookingId={props.bookingId}
					modelId={context.modal.getData(addModalId)}
					onSave={() => onUpdate(addModalId)}
				/>
			) : <></>}
		</ModalPortal>
		<ModalPortal name={viewModalId} title="pages.accommodation.event.view">
			{searchParams.has(viewModalId) ? (
				<EventView updateUrl={addModalId} name="accommodationEventDatalistView" model={context.modal.getData(viewModalId)} onUpdate={() => onUpdate(viewModalId)} />
			) : <></>}
		</ModalPortal>
	</>
};

export default AccommodationEventDatalist;
